import React, { useEffect, useState } from "react";
import { api, fileUrl, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t, formatMoney } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QrCode, FileDown, FileText, Calendar } from "lucide-react";
import { logWarn } from "@/lib/logger";

const pick = (dict, lang, fallback) => dict[lang] || fallback;

const L = {
  qr_title: { it: "Scansiona per bonifico SEPA", en: "Scan to wire (SEPA)", he: "סרקו להעברת SEPA", ru: "Сканируйте для SEPA" },
  qr_desc: {
    it: "Compatibile con la maggior parte delle app bancarie italiane ed europee. IBAN, importo e causale già precompilati.",
    en: "Works with most EU banking apps. IBAN, amount and reference are pre-filled.",
    he: "תואם רוב אפליקציות הבנקאות. IBAN, סכום ותיאור מוזנים אוטומטית.",
    ru: "Совместимо с большинством банковских приложений. IBAN, сумма и описание уже заполнены.",
  },
  generate_month: { it: "Genera mese corrente", en: "Generate current month", he: "צרו לחודש הנוכחי", ru: "Создать за месяц" },
  no_reports: { it: "Nessun report generato", en: "No reports yet", he: "אין דוחות עדיין", ru: "Отчётов пока нет" },
  monthly_reports_title: { it: "Report mensili", en: "Monthly reports", he: "דוחות חודשיים", ru: "Ежемесячные отчёты" },
  monthly_reports_desc: {
    it: "Riepilogo del mese: mandati firmati, trattative chiuse, fondi in escrow e fee incassate. Un click, un PDF firmabile.",
    en: "Monthly recap: signed mandates, closed deals, escrow balances and collected fees. One click, one signable PDF.",
    he: "סיכום חודשי: מנדטים חתומים, עסקאות סגורות, יתרות נאמנות ועמלות. קליק אחד, PDF חתום.",
    ru: "Ежемесячный отчёт: подписанные мандаты, закрытые сделки, эскроу и полученные комиссии. Один клик — один подписной PDF.",
  },
};

/** SEPA/EPC QR code viewer for a deal. */
export function WireQRButton({ deal }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!open) return;
    let objectUrl;
    (async () => {
      try {
        const resp = await api.get(`/deals/${deal.id}/wire-qr`, { responseType: "blob" });
        objectUrl = URL.createObjectURL(resp.data);
        setSrc(objectUrl);
      } catch (e) { toast.error("QR error"); }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open, deal.id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`qr-btn-${deal.id}`}
          className="border-[#B8A98F] text-[#2E2A22] bg-transparent hover:bg-[#EBE3D3] rounded-full">
          <QrCode className="h-3.5 w-3.5 me-1.5" />
          {t("wire_qr_short", lang)}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {pick(L.qr_title, lang, "Scan to wire (SEPA)")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center py-4">
          {src ? (
            <img src={src} alt="SEPA QR" className="h-64 w-64 bg-white p-3 rounded-lg" data-testid="wire-qr-image" />
          ) : (
            <div className="h-64 w-64 bg-[#EBE3D3]/40 animate-pulse rounded-lg" />
          )}
          <p className="text-xs text-[#7A6E5A] mt-4 text-center leading-relaxed max-w-xs">
            {pick(L.qr_desc, lang, "Works with most EU banking apps. IBAN, amount and reference are pre-filled.")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ContractPdfButton({ deal }) {
  const { lang } = useApp();
  if (!deal.direct_contract_signed) return null;
  const download = async () => {
    try {
      const resp = await api.get(`/deals/${deal.id}/direct-contract/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto-diretto-${deal.id.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF error"); }
  };
  return (
    <Button size="sm" variant="outline" onClick={download} data-testid={`contract-pdf-${deal.id}`}
      className="border-emerald-600/50 text-emerald-400 bg-transparent hover:bg-emerald-950 rounded-full">
      <FileDown className="h-3.5 w-3.5 me-1.5" />
      PDF
    </Button>
  );
}

export function MonthlyReports() {
  const { lang } = useApp();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/admin/reports/monthly"); setItems(data); }
    catch (e) { logWarn("MonthlyReports load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reports/monthly");
      toast.success(`Report ${data.month}/${data.year} generato`);
      await load();
    } catch (e) { toast.error("Error"); }
    finally { setBusy(false); }
  };

  const download = async (fileId, filename) => {
    const resp = await api.get(`/files/${fileId}`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-6" data-testid="monthly-reports">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A880] mb-1">
            {t("reports_label", lang)}
          </p>
          <h3 className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {t("monthly_reports", lang)}
          </h3>
        </div>
        <Button onClick={generate} disabled={busy} data-testid="generate-monthly-report"
          className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full disabled:opacity-50">
          <Calendar className="h-4 w-4 me-2" />
          {busy ? "…" : pick(L.generate_month, lang, "Generate current month")}
        </Button>
      </div>
      {!items.length && (
        <p className="text-sm text-[#9A8B72] py-10 text-center">
          {pick(L.no_reports, lang, "No reports yet")}
        </p>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((r) => (
          <div key={r.id} className="rounded-lg border border-[#D8CFBB] bg-[#F5F0E6] p-5" data-testid={`report-${r.year}-${r.month}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-[#C5A880]" />
                <div>
                  <p className="text-lg font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                    {String(r.month).padStart(2, "0")}/{r.year}
                  </p>
                  <p className="text-[11px] text-[#9A8B72] font-mono">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <Button size="sm" onClick={() => download(r.file_id, `aaron-report-${r.year}-${String(r.month).padStart(2,"0")}.pdf`)}
                data-testid={`download-report-${r.year}-${r.month}`}
                className="bg-transparent border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880]/10 rounded-full">
                <FileDown className="h-3.5 w-3.5 me-1.5" /> PDF
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center pt-3 border-t border-[#E5DDC9]">
              <div>
                <p className="text-[10px] tracking-widest uppercase text-[#9A8B72]">Deals</p>
                <p className="text-lg text-[#1A1610]">{r.stats?.deals_count ?? 0}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-widest uppercase text-[#9A8B72]">Revenue</p>
                <p className="text-lg text-[#C5A880]">{formatMoney(r.stats?.total_revenue ?? 0, lang)}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-widest uppercase text-[#9A8B72]">Escrow</p>
                <p className="text-lg text-[#1A1610]">{formatMoney(r.stats?.escrow_held ?? 0, lang)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
